/**
 * Monaco TypeScript configuration.
 *
 * Configures Monaco's built-in TypeScript worker for JSX/TSX and stubs the one
 * external with no mount behind it (React's jsx-runtime).
 *
 * Bear's own source is NOT loaded here, and neither are three's types. Both
 * reach the editor as the mounted packages they already are (`node_modules/bear`,
 * `node_modules/@types/three`, registered by MonacoEditor from the filesystem),
 * so there is one copy of each. A second copy, whether generated into
 * `public/types/bear` or hand-written inline, red-underlines code that runs.
 */

import type { Monaco } from "@monaco-editor/react";

/** Stub the externals that have no mounted types. */
function loadExternalTypes(monaco: Monaco) {
  const tsDefaults = monaco.languages.typescript.typescriptDefaults;

  const jsxRuntimeTypes = `
declare module 'react/jsx-runtime' {
  export namespace JSX {
    type Element = any;
    interface IntrinsicElements {
      [elemName: string]: any;
    }
  }
  export function jsx(type: any, props: any, key?: any): any;
  export function jsxs(type: any, props: any, key?: any): any;
  export const Fragment: any;
}

declare module 'react' {
  export type FC<P = {}> = (props: P) => JSX.Element | null;
  export type ReactNode = any;
  export function createElement(type: any, props?: any, ...children: any[]): any;
  export const Fragment: any;
}
`;
  tsDefaults.addExtraLib(
    jsxRuntimeTypes,
    "file:///node_modules/react/jsx-runtime.d.ts",
  );
}

/**
 * Configure Monaco's TypeScript worker for JSX/TSX editing.
 *
 * @param monaco - Monaco instance from the editor
 */
export function configureMonacoTypeScript(monaco: Monaco) {
  const tsDefaults = monaco.languages.typescript.typescriptDefaults;
  const jsDefaults = monaco.languages.typescript.javascriptDefaults;

  // Configure compiler options for JSX
  const compilerOptions: Parameters<typeof tsDefaults.setCompilerOptions>[0] = {
    target: monaco.languages.typescript.ScriptTarget.ESNext,
    module: monaco.languages.typescript.ModuleKind.ESNext,
    jsx: monaco.languages.typescript.JsxEmit.ReactJSX,
    jsxImportSource: "bear/engine",
    moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
    allowNonTsExtensions: true,
    baseUrl: "file:///",
    // `three` needs no entry: node resolution finds node_modules/@types itself.
    paths: {
      bear: ["node_modules/bear/index.ts"],
      "bear/*": ["node_modules/bear/*"],
      "@/*": ["*"],
    },
    strict: false, // Lenient for user code
    esModuleInterop: true,
    skipLibCheck: true,
  };

  tsDefaults.setCompilerOptions(compilerOptions);
  jsDefaults.setCompilerOptions(compilerOptions);

  // Diagnostics aren't configured here: they're gated on the workspace being
  // registered, which `forest/monaco-editor/workspace-types` owns.
  loadExternalTypes(monaco);
}
